import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FaShieldAlt, FaExternalLinkAlt, FaExclamationTriangle, FaRobot, FaUserCircle, FaFile, FaFilePdf, FaFileWord, FaFileAlt, FaCopy } from 'react-icons/fa';
import { API_URL } from '../config';

const MessageContainer = styled.div`
  margin-bottom: 20px;
  padding: 0 20px;
`;

const MessageBubble = styled.div`
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 8px;
  line-height: 1.5;
  position: relative;
  ${props => props.role === 'user' ? `
    background-color: #e6f2ff;
    margin-left: auto;
  ` : `
    background-color: #f5f5f5;
    margin-right: auto;
  `}
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 5px;
`;

const SenderName = styled.div`
  font-weight: 500;
  margin-right: 10px;
  display: flex;
  align-items: center;
`;

const Timestamp = styled.div`
  font-size: 12px;
  color: #666;
`;

const BlockedMessage = styled.div`
  margin-top: 10px;
  padding: 15px;
  background-color: #fff1f0;
  border: 1px solid #ffccc7;
  border-radius: 4px;
  font-size: 14px;
  color: #cf1322;
  display: flex;
  flex-direction: column;
`;

const BlockedReason = styled.div`
  margin-top: 10px;
  padding: 10px;
  background-color: #fff7e6;
  border-radius: 4px;
  font-size: 14px;
  color: #d46b08;
  border-left: 4px solid #fa8c16;
`;

const BlockedIcon = styled.span`
  color: #f5222d;
  margin-right: 8px;
`;

const PolicySnippet = styled.div`
  font-style: italic;
  margin-top: 0.5rem;
  padding: 0.1rem;
  background-color: #fef2f2;
  border-left: 3px solid #ef4444;
  font-size: 14px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
`;

const RuleDocumentReference = styled.div`
  margin-top: 0.5rem;
  padding: 0.1rem;
  background-color: #fef2f2;
  border-radius: 4px;
  font-size: 13px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
`;

const RuleDocumentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  font-size: 14px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
`;

const RuleDocumentLink = styled.a`
  color: #ff8080 !important;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 13px;
  margin-top: 0.25rem;
  font-weight: 500;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  
  &:hover {
    text-decoration: underline;
    color: #ff9999 !important;
  }
`;

const SearchStats = styled.div`
  margin-top: 5px;
  font-size: 12px;
  color: #666;
`;

const AnimatedText = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 14px;
  line-height: 1.6;
`;

const Cursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 14px;
  background-color: #0084ff;
  margin-left: 2px;
  animation: blink 1s infinite;
  
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`;

const MarkdownContent = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  
  p {
    margin: 0 0 1em 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  ul, ol {
    margin: 0 0 1em 0;
    padding-left: 1.5em;
  }
  
  li {
    margin-bottom: 0.5em;
  }
  
  code {
    background-color: rgba(0, 0, 0, 0.05);
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 85%;
  }
  
  pre {
    margin: 1em 0;
    padding: 0;
    overflow: hidden;
    border-radius: 6px;
  }
  
  pre div {
    margin: 0 !important;
  }
  
  h1, h2, h3, h4, h5, h6 {
    margin-top: 1.5em;
    margin-bottom: 0.5em;
    font-weight: 600;
    line-height: 1.25;
  }
  
  h1 {
    font-size: 1.5em;
  }
  
  h2 {
    font-size: 1.3em;
  }
  
  h3 {
    font-size: 1.1em;
  }
  
  a {
    color: #0366d6;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  blockquote {
    margin: 1em 0;
    padding: 0 1em;
    color: #6a737d;
    border-left: 0.25em solid #dfe2e5;
  }
  
  hr {
    height: 0.25em;
    padding: 0;
    margin: 1.5em 0;
    background-color: #e1e4e8;
    border: 0;
  }
  
  table {
    border-collapse: collapse;
    margin: 1em 0;
    width: 100%;
    overflow: auto;
  }
  
  table th, table td {
    padding: 6px 13px;
    border: 1px solid #dfe2e5;
  }
  
  table tr {
    background-color: #fff;
    border-top: 1px solid #c6cbd1;
  }
  
  table tr:nth-child(2n) {
    background-color: #f6f8fa;
  }
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
  
  span {
    display: inline-block;
    width: 8px;
    height: 8px;
    margin-right: 4px;
    background-color: #10a37f;
    border-radius: 50%;
    animation: typing 1.4s infinite both;
    
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
  
  @keyframes typing {
    0% {
      opacity: 0.2;
      transform: scale(0.8);
    }
    20% {
      opacity: 1;
      transform: scale(1);
    }
    100% {
      opacity: 0.2;
      transform: scale(0.8);
    }
  }
`;

const MessageContent = styled.div`
  font-size: 14px;
  margin-top: 4px;
  word-break: break-word;
  
  p {
    margin: 0 0 10px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  pre {
    margin: 10px 0;
    border-radius: 4px;
    overflow-x: auto;
  }
  
  code {
    font-family: 'Fira Code', monospace;
    font-size: 13px;
  }
  
  ul, ol {
    margin: 10px 0;
    padding-left: 20px;
  }
`;

const DocumentAttachment = styled.div`
  display: flex;
  align-items: center;
  background-color: #f0f7ff;
  border-radius: 4px;
  padding: 8px 12px;
  margin-top: 8px;
  border: 1px solid #d0e8ff;
`;

const DocumentIcon = styled.div`
  margin-right: 10px;
  color: #0066cc;
  font-size: 20px;
`;

const DocumentInfo = styled.div`
  flex: 1;
`;

const DocumentName = styled.div`
  font-weight: 500;
  font-size: 13px;
`;

const DocumentMeta = styled.div`
  font-size: 11px;
  color: #666;
  margin-top: 2px;
`;

const CodeBlock = styled.div`
  position: relative;
  margin: 1rem 0;
  border-radius: 6px;
  overflow: hidden;
`;

const CopyButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  svg {
    margin-right: 4px;
  }
`;

const CopySuccess = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(16, 163, 127, 0.8);
  color: white;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
`;

// Word-by-word animation component
const WordByWordText = ({ text, speed = 30, onComplete }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [wordIndex, setWordIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const words = useRef(text.split(' '));
  
  useEffect(() => {
    if (wordIndex < words.current.length) {
      const timer = setTimeout(() => {
        setDisplayedText(prev => prev + (wordIndex > 0 ? ' ' : '') + words.current[wordIndex]);
        setWordIndex(wordIndex + 1);
      }, speed);
      
      return () => clearTimeout(timer);
    } else if (!isComplete) {
      setIsComplete(true);
      if (onComplete) onComplete();
    }
  }, [wordIndex, speed, isComplete, onComplete]);
  
  return (
    <AnimatedText>
      {displayedText}
      {!isComplete && <Cursor />}
    </AnimatedText>
  );
};

const ChatMessage = ({ id, role, content, timestamp, isNewMessage, messageIsBlocked, is_blocked, isBlocked, violatedDocument, documentId, isLoading }) => {
  // Check if this message has already been animated
  const messageKey = `animated_${id}`;
  const hasBeenAnimated = localStorage.getItem(messageKey);
  
  // Only animate if this is a new message and hasn't been animated before
  const [showAnimation, setShowAnimation] = useState(isNewMessage === true && role === 'assistant' && !hasBeenAnimated);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [documentInfo, setDocumentInfo] = useState(null);
  const [copySuccess, setCopySuccess] = useState(null);
  
  useEffect(() => {
    // If this message has a document ID, fetch document info
    if (documentId) {
      // In a real implementation, you would fetch document info from the server
      // For now, we'll just set some mock data
      setDocumentInfo({
        id: documentId,
        name: `Document-${documentId.substring(0, 8)}`,
        type: 'pdf',
        uploadTime: new Date().toISOString()
      });
    }
  }, [documentId]);
  
  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}, ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
  };
  
  // Copy code to clipboard
  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code).then(
      () => {
        setCopySuccess('Copied!');
        setTimeout(() => setCopySuccess(null), 2000);
      },
      () => {
        setCopySuccess('Failed to copy');
        setTimeout(() => setCopySuccess(null), 2000);
      }
    );
  };
  
  // Custom renderer for code blocks
  const components = {
    code({node, inline, className, children, ...props}) {
      const match = /language-(\w+)/.exec(className || '');
      const code = String(children).replace(/\n$/, '');
      
      if (!inline && match) {
        return (
          <CodeBlock>
            {copySuccess ? (
              <CopySuccess>{copySuccess}</CopySuccess>
            ) : (
              <CopyButton onClick={() => copyToClipboard(code)}>
                <FaCopy size={12} /> Copy
              </CopyButton>
            )}
            <SyntaxHighlighter
              style={vscDarkPlus}
              language={match[1]}
              PreTag="div"
              {...props}
            >
              {code}
            </SyntaxHighlighter>
          </CodeBlock>
        );
      }
      
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
  };
  
  // Handle animation completion
  const handleAnimationComplete = () => {
    setAnimationComplete(true);
    // Store in localStorage that this message has been animated
    if (id) {
      localStorage.setItem(messageKey, 'true');
    }
  };
  
  // Skip animation and show full content
  const handleSkipAnimation = () => {
    setShowAnimation(false);
    // Store in localStorage that this message has been animated
    if (id) {
      localStorage.setItem(messageKey, 'true');
    }
  };
  
  return (
    <MessageContainer>
      <MessageBubble role={role}>
        <MessageHeader>
          <SenderName>
            {role === 'user' ? <FaUserCircle style={{ marginRight: '8px', color: '#4285f4' }} /> : <FaRobot style={{ marginRight: '8px', color: '#10a37f' }} />}
            {role === 'user' ? 'You' : 'Assistant'}
          </SenderName>
          <Timestamp>{formatTime(timestamp)}</Timestamp>
        </MessageHeader>
        
        {documentId && role === 'assistant' && (
          <DocumentAttachment>
            <DocumentIcon>
              {documentInfo.type === 'pdf' ? <FaFilePdf /> : documentInfo.type === 'word' ? <FaFileWord /> : <FaFileAlt />}
            </DocumentIcon>
            <DocumentInfo>
              <DocumentName>{documentInfo.name}</DocumentName>
              <DocumentMeta>Uploaded at {documentInfo.uploadTime}</DocumentMeta>
            </DocumentInfo>
          </DocumentAttachment>
        )}
        
        {documentId && role === 'user' && (
          <DocumentAttachment>
            <DocumentIcon>
              <FaFile />
            </DocumentIcon>
            <DocumentInfo>
              <DocumentName>Uploaded Document</DocumentName>
              <DocumentMeta>Document ID: {documentId.substring(0, 8)}...</DocumentMeta>
            </DocumentInfo>
          </DocumentAttachment>
        )}
        
        {isLoading ? (
          <TypingIndicator>
            <span></span>
            <span></span>
            <span></span>
          </TypingIndicator>
        ) : role === 'assistant' ? (
          <div onClick={handleSkipAnimation}>
            {showAnimation && !animationComplete ? (
              <WordByWordText 
                text={content} 
                speed={30} 
                onComplete={handleAnimationComplete} 
              />
            ) : (
              <MarkdownContent>
                <ReactMarkdown components={components}>
                  {content}
                </ReactMarkdown>
              </MarkdownContent>
            )}
          </div>
        ) : (
          <MarkdownContent>
            <ReactMarkdown components={components}>
              {content}
            </ReactMarkdown>
          </MarkdownContent>
        )}
        
        {(messageIsBlocked === true || is_blocked === 1 || isBlocked === true) && (
          <BlockedMessage>
            <div>
              {content.includes('AI usage rules') ? (
                <strong><BlockedIcon><FaExclamationTriangle /></BlockedIcon>This message was blocked by AI usage rules.</strong>
              ) : (
                <strong><BlockedIcon><FaExclamationTriangle /></BlockedIcon>This message was blocked by our content policy.</strong>
              )}
            </div>
            
            {/* Show the reason for blocking */}
            <BlockedReason>
              <strong>Reason:</strong> {violatedDocument?.reason || content || "Violated content policy"}
            </BlockedReason>
            
            {/* {violatedDocument && <PolicySnippet>{violatedDocument.policySnippet}</PolicySnippet>} */}
                
                {/* Display violated document reference if available */}
                {/* {violatedDocument && (
                  <RuleDocumentReference>
                    <RuleDocumentHeader>
                      <FaShieldAlt /> Rule Document Reference:
                    </RuleDocumentHeader>
                    <div>
                      <strong>{violatedDocument.title || violatedDocument.filename}</strong>
                    </div>
                    <RuleDocumentLink 
                      href={`${API_URL}/documents/${violatedDocument.id}/view`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent event bubbling
                        window.open(`${API_URL}/documents/${violatedDocument.id}/view`, '_blank');
                      }}
                    >
                      View Document <FaExternalLinkAlt size={10} />
                    </RuleDocumentLink>
                  </RuleDocumentReference>
                )} */}
          </BlockedMessage>
        )}
        
        {role === 'assistant' && (
          <SearchStats>
            {isLoading ? 'Thinking...' : ''}
          </SearchStats>
        )}
      </MessageBubble>
    </MessageContainer>
  );
};

export default ChatMessage;
